/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { PlaylistEntry } from '@/playback/PlaylistController'
import type { ImageFields } from '@/service/fragments/imageFieldsFrag'
import { modifyImage } from '@/util/ImageUrl'

// Square artwork edge lengths (px) requested from MediaCentral. Handing the OS
// media session several sizes lets it pick the resolution the lock screen /
// control center actually paints instead of over-fetching a full-res cover.
const ARTWORK_SIZES = [96, 256, 512] as const

// Bundled PWA icons (see html/manifest.json). Root-relative so they resolve
// against the app origin at runtime — a real fallback cover when an entry has
// no artwork, rather than an empty `src` that renders blank OS media art.
const PLACEHOLDER_ARTWORK: MediaImage[] = [96, 192, 512].map((size) => ({
    src: `/icons/icon-${size}.webp`,
    sizes: `${size}x${size}`,
    type: 'image/webp',
}))

function artworkFor(image: ImageFields | null): MediaImage[] {
    if (image?.url == null) return PLACEHOLDER_ARTWORK
    const sized = ARTWORK_SIZES.map((size): MediaImage | null => {
        const scaled = modifyImage(image, { scaleToSquare: size, dominantBackground: true })
        return scaled.url != null ? { src: scaled.url, sizes: `${size}x${size}` } : null
    }).filter((entry): entry is MediaImage => entry != null)
    return sized.length > 0 ? sized : PLACEHOLDER_ARTWORK
}

/**
 * Shapes the OS media-session metadata (lock screen / control center) for a
 * queue entry.
 *
 * PlaylistEntry is already the per-content-type projection: the track and
 * podcast-episode mappers fold each type's fields into these flat labels — a
 * podcast episode's `artistName` is its author and `albumTitle` its show, a
 * track's are the contributing artist and album — so reading the flat labels
 * here surfaces the right field per content type without re-deriving it.
 */
export function lockScreenMetadataInit(entry: PlaylistEntry): MediaMetadataInit {
    return {
        title: entry.title,
        // exactOptionalPropertyTypes: omit an empty label rather than emit ''.
        ...(entry.artistName ? { artist: entry.artistName } : null),
        ...(entry.albumTitle ? { album: entry.albumTitle } : null),
        artwork: artworkFor(entry.image),
    }
}
